import React, {
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, useDroppable } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { Season } from "@mysnowscape/snow-lib";

import PencilIcon from "../../../../../resources/pencil.svg";
import TagIcon from "../../../../../resources/tag.svg";
import { DragDropIcon } from "../../RoutePage/UI/DragDropIcon";
import { resolveRouteColorPalette } from "../../../../../components/Maps/ActiveRoutes/routeColor";
import { persistedRouteType } from "../../../../../utils/routeType";
import RouteSelectionCheckbox, {
  type RouteSelectionState,
} from "./RouteSelectionCheckbox";
import {
  buildRouteGroupTree,
  descendantRoutes,
  type GroupedRoute,
  type RouteGroupNode,
} from "../lib/routeGroups";
import { resolveGroupDragEnd } from "../lib/routeGroupDrag";

export type RoutesPageRoute = GroupedRoute & {
  jobIds?: string[];
  jobs?: unknown[];
  color?: string;
  isActive?: boolean;
  expiresAt?: string | null;
  details?: string;
};

type DragEndEvent = Parameters<
  NonNullable<ComponentProps<typeof DragDropProvider>["onDragEnd"]>
>[0];

function selectionState(
  routes: RoutesPageRoute[],
  selectedRouteIds: Set<string>,
): RouteSelectionState {
  const ids = new Set(routes.map((route) => route._id));
  if (ids.size === 0) return "unchecked";
  let selected = 0;
  for (const id of Array.from(ids)) {
    if (selectedRouteIds.has(id)) selected += 1;
  }
  if (selected === 0) return "unchecked";
  return selected === ids.size ? "checked" : "indeterminate";
}

function Chevron({ open }: { open: boolean }) {
  return <span aria-hidden>{open ? "▾" : "▸"}</span>;
}

function sortableGroupKey(season: Season, path: string | null): string {
  return JSON.stringify([season, path]);
}

function parseSortableGroupKey(
  value: unknown,
): { season: Season; path: string | null } | null {
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (
      !Array.isArray(parsed) ||
      parsed.length !== 2 ||
      (parsed[0] !== Season.landscape && parsed[0] !== Season.snow) ||
      (parsed[1] !== null && typeof parsed[1] !== "string")
    ) {
      return null;
    }
    return { season: parsed[0] as Season, path: parsed[1] };
  } catch {
    return null;
  }
}

function routesAtPath(
  groups: RouteGroupNode<RoutesPageRoute>[],
  ungrouped: RoutesPageRoute[],
  path: string | null,
): RoutesPageRoute[] {
  if (path === null) return ungrouped;
  const stack = [...groups];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if (node.path === path) return node.routes;
    stack.push(...node.children);
  }
  return [];
}

function RouteRow({
  route,
  index,
  depth,
  sourcePath,
  selected,
  busy,
  saving,
  onToggle,
  onOpen,
}: {
  route: RoutesPageRoute;
  index: number;
  depth: number;
  sourcePath: string | null;
  selected: boolean;
  busy: boolean;
  saving: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const { ref, handleRef, isDropTarget } = useSortable({
    id: `group-route-drag-${route._id}-${sourcePath ?? "ungrouped"}`,
    index,
    group: sortableGroupKey(route.season, sourcePath),
    type: "group-route",
    collisionPriority: 30,
    disabled: busy,
    modifiers: [RestrictToVerticalAxis],
    data: { routeId: route._id, season: route.season, sourcePath, index },
  });

  const stopCount = route.jobIds?.length ?? route.jobs?.length ?? 0;
  return (
    <li
      ref={ref}
      data-testid={`group-route-${route._id}-${sourcePath ?? "ungrouped"}`}
      data-route-drop-target={isDropTarget ? "true" : undefined}
      aria-busy={saving}
      className={`route-tree-row flex min-h-9 items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 ${
        route.isActive === false ? "opacity-60" : ""
      }`}
    >
      <RouteSelectionCheckbox
        state={selected ? "checked" : "unchecked"}
        label={`Select ${route.name}`}
        testId={`group-route-check-${route._id}`}
        disabled={busy}
        onChange={onToggle}
      />
      <span className="shrink-0" style={{ width: depth * 20 }} aria-hidden />
      <button
        type="button"
        ref={handleRef}
        className="route-tree-row-control cursor-grab touch-none rounded px-0.5 active:cursor-grabbing disabled:cursor-wait"
        aria-label={`Move ${route.name}`}
        disabled={busy}
        title={busy ? "Still loading. Please wait before dragging." : undefined}
        data-testid={`group-route-drag-handle-${route._id}`}
        onClick={(event) => event.stopPropagation()}
      >
        <DragDropIcon />
      </button>
      <span
        className="flex h-4 w-4 shrink-0 items-center justify-center"
        aria-hidden
        data-testid={`group-route-color-${route._id}`}
      >
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: resolveRouteColorPalette(route.color).hex }}
        />
      </span>
      <button
        type="button"
        className="min-w-0 truncate text-left text-sm font-semibold text-snow-primary hover:underline"
        onClick={onOpen}
      >
        {route.name}
      </button>
      {persistedRouteType(route) === "temporary" ? (
        <span
          className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
          data-testid="route-temporary-label"
        >
          One-time
        </span>
      ) : null}
      <span className="shrink-0 text-xs text-slate-500">
        {stopCount} {stopCount === 1 ? "stop" : "stops"}
      </span>
      {saving ? (
        <span
          role="status"
          className="shrink-0 text-xs text-sky-700"
          data-testid={`group-route-loading-${route._id}`}
        >
          Loading...
        </span>
      ) : null}
      {route.isActive === false ? (
        <span className="shrink-0 text-[10px] font-semibold uppercase text-rose-600">
          Inactive
        </span>
      ) : null}
    </li>
  );
}

function GroupDropRow({
  node,
  season,
  depth,
  expanded,
  activePath,
  siblingNames,
  busy,
  pendingRouteId,
  selectedRouteIds,
  onToggleExpanded,
  onToggleRoutes,
  onActivate,
  onAdd,
  onRename,
  onDelete,
  onToggleRoute,
  onReorderRoute,
  onOpenRoute,
}: {
  node: RouteGroupNode<RoutesPageRoute>;
  season: Season;
  depth: number;
  expanded: Set<string>;
  activePath: string | null;
  siblingNames: string[];
  busy: boolean;
  pendingRouteId: string | null;
  selectedRouteIds: Set<string>;
  onToggleExpanded: (path: string) => void;
  onToggleRoutes: (routes: RoutesPageRoute[]) => void;
  onActivate: (path: string) => void;
  onAdd: (path: string, siblingNames: string[]) => void;
  onRename: (
    node: RouteGroupNode<RoutesPageRoute>,
    siblingNames: string[],
  ) => void;
  onDelete: (node: RouteGroupNode<RoutesPageRoute>) => void;
  onToggleRoute: (route: RoutesPageRoute) => void;
  onReorderRoute: (route: RoutesPageRoute, targetRouteId: string) => void;
  onOpenRoute: (route: RoutesPageRoute) => void;
}) {
  const isOpen = expanded.has(`${season}:${node.path}`);
  const routes = descendantRoutes(node);
  const { ref, isDropTarget } = useDroppable({
    id: `group-drop-${season}-${node.path}`,
    type: "route-group",
    accept: "group-route",
    collisionPriority: 40,
    disabled: busy,
    data: { season, path: node.path },
  });
  const childSiblingNames = node.children.map((child) => child.name);

  return (
    <li data-testid={`route-group-${season}-${node.path}`}>
      <div
        ref={ref}
        data-group-row-path={node.path}
        data-testid={`route-group-drop-${season}-${node.path}`}
        className={`route-tree-row group flex min-h-9 items-center gap-2 rounded-lg px-2 py-1.5 transition ${
          isDropTarget ? "bg-sky-100 ring-2 ring-sky-300" : "hover:bg-slate-50"
        }`}
        onClick={() => onActivate(node.path)}
      >
        <RouteSelectionCheckbox
          state={selectionState(routes, selectedRouteIds)}
          label={`Select Group ${node.name}`}
          testId={`route-group-check-${season}-${node.path}`}
          disabled={busy || routes.length === 0}
          onChange={() => onToggleRoutes(routes)}
        />
        <span className="shrink-0" style={{ width: depth * 20 }} aria-hidden />
        <button
          type="button"
          className="ml-2 w-4 shrink-0 text-xs text-slate-500"
          aria-label={`${isOpen ? "Collapse" : "Expand"} Group ${node.name}`}
          aria-expanded={isOpen}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpanded(node.path);
          }}
        >
          <Chevron open={isOpen} />
        </button>
        <img
          src={TagIcon}
          alt=""
          className="h-4 w-4 shrink-0"
          aria-hidden
          data-testid="group-tag-icon"
        />
        <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
          {node.name}
        </span>
        <button
          type="button"
          className="route-tree-row-control shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-700"
          aria-label={`Rename Group ${node.name}`}
          data-testid={`route-group-rename-${season}-${node.path}`}
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onRename(node, siblingNames);
          }}
        >
          <img src={PencilIcon} alt="" className="h-3.5 w-3.5" aria-hidden />
        </button>
        <div
          className={`ml-auto flex items-center gap-2 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${
            activePath === node.path ? "opacity-100" : "opacity-0"
          }`}
        >
          <button
            type="button"
            className="text-xs font-medium text-sky-700 hover:underline"
            data-testid={`route-group-add-${season}-${node.path}`}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onAdd(node.path, childSiblingNames);
            }}
          >
            Add
          </button>
          <button
            type="button"
            className="text-xs font-medium text-rose-700 hover:underline"
            data-testid={`route-group-delete-${season}-${node.path}`}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(node);
            }}
          >
            Delete
          </button>
        </div>
      </div>
      {isOpen ? (
        <ul className="list-none">
          {node.children.map((child) => (
            <GroupDropRow
              key={child.path}
              node={child}
              season={season}
              depth={depth + 1}
              expanded={expanded}
              activePath={activePath}
              siblingNames={childSiblingNames}
              busy={busy}
              pendingRouteId={pendingRouteId}
              selectedRouteIds={selectedRouteIds}
              onToggleExpanded={onToggleExpanded}
              onToggleRoutes={onToggleRoutes}
              onActivate={onActivate}
              onAdd={onAdd}
              onRename={onRename}
              onDelete={onDelete}
              onToggleRoute={onToggleRoute}
              onReorderRoute={onReorderRoute}
              onOpenRoute={onOpenRoute}
            />
          ))}
          {node.routes.map((route, index) => (
            <RouteRow
              key={`${route._id}-${node.path}`}
              route={route}
              index={index}
              depth={depth + 1}
              sourcePath={node.path}
              selected={selectedRouteIds.has(route._id)}
              busy={busy}
              saving={pendingRouteId === route._id}
              onToggle={() => onToggleRoute(route)}
              onOpen={() => onOpenRoute(route)}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function SeasonDropRow({
  season,
  routes,
  selectedRouteIds,
  busy,
  open,
  siblingNames,
  onToggleExpanded,
  onToggleRoutes,
  onAddGroup,
}: {
  season: Season;
  routes: RoutesPageRoute[];
  selectedRouteIds: Set<string>;
  busy: boolean;
  open: boolean;
  siblingNames: string[];
  onToggleExpanded: () => void;
  onToggleRoutes: () => void;
  onAddGroup: (parentPath: null, siblingNames: string[]) => void;
}) {
  const { ref, isDropTarget } = useDroppable({
    id: `season-drop-${season}`,
    type: "route-group",
    accept: "group-route",
    collisionPriority: 35,
    disabled: busy,
    data: { season, path: null },
  });

  return (
    <div
      ref={ref}
      data-testid={`season-drop-${season}`}
      className={`flex min-h-10 items-center gap-2 rounded-lg px-2 py-2 ${
        isDropTarget ? "bg-sky-100 ring-2 ring-sky-300" : "bg-slate-100"
      }`}
    >
      <RouteSelectionCheckbox
        state={selectionState(routes, selectedRouteIds)}
        label={`Select ${season}`}
        testId={`route-season-check-${season}`}
        disabled={busy || routes.length === 0}
        onChange={onToggleRoutes}
      />
      <button
        type="button"
        className="w-4 text-xs text-slate-500"
        aria-label={`${open ? "Collapse" : "Expand"} ${season}`}
        aria-expanded={open}
        onClick={onToggleExpanded}
      >
        <Chevron open={open} />
      </button>
      <span className="text-base font-bold capitalize text-snow-primary">
        {season}
      </span>
      <button
        type="button"
        className="ml-auto rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        data-testid={`route-group-add-root-${season}`}
        disabled={busy}
        onClick={() => onAddGroup(null, siblingNames)}
      >
        New Group
      </button>
    </div>
  );
}

export default function RoutesGroupTree({
  season,
  routes,
  transientPaths,
  selectedRouteIds,
  busy,
  pendingRouteId,
  onSelectionChange,
  onAddGroup,
  onRenameGroup,
  onDeleteGroup,
  onMoveRoute,
  onReorderRoute,
  onOpenRoute,
}: {
  season: Season;
  routes: RoutesPageRoute[];
  transientPaths: Set<string>;
  selectedRouteIds: Set<string>;
  busy: boolean;
  pendingRouteId: string | null;
  onSelectionChange: (next: Set<string>) => void;
  onAddGroup: (parentPath: string | null, siblingNames: string[]) => void;
  onRenameGroup: (
    node: RouteGroupNode<RoutesPageRoute>,
    siblingNames: string[],
  ) => void;
  onDeleteGroup: (node: RouteGroupNode<RoutesPageRoute>) => void;
  onMoveRoute: (
    route: RoutesPageRoute,
    sourcePath: string | null,
    targetPath: string | null,
  ) => void;
  onReorderRoute: (route: RoutesPageRoute, targetRouteId: string) => void;
  onOpenRoute: (route: RoutesPageRoute) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { groups, ungrouped } = useMemo(
    () => buildRouteGroupTree(routes, transientPaths),
    [routes, transientPaths],
  );
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set([`${season}:__season__`]),
  );
  const knownGroupPaths = useRef(new Set<string>());
  const [activePath, setActivePath] = useState<string | null>(null);
  const seasonOpen = expanded.has(`${season}:__season__`);
  // Sorting moves DOM nodes directly, so route changes remount the provider.
  // Expansion stays out of this key to preserve the focused toggle button.
  const dragLayoutKey = useMemo(
    () =>
      JSON.stringify(
        routes.map((route) => [route._id, route.tags ?? [], route.order]),
      ),
    [routes],
  );

  useEffect(() => {
    const currentPaths = new Set<string>();
    const collectPaths = (nodes: RouteGroupNode<RoutesPageRoute>[]) => {
      for (const node of nodes) {
        currentPaths.add(`${season}:${node.path}`);
        collectPaths(node.children);
      }
    };
    collectPaths(groups);
    setExpanded((previous) => {
      const next = new Set(previous);
      let changed = false;
      const seasonKey = `${season}:__season__`;
      if (!next.has(seasonKey)) {
        next.add(seasonKey);
        changed = true;
      }
      for (const path of Array.from(currentPaths)) {
        if (!knownGroupPaths.current.has(path) && !next.has(path)) {
          next.add(path);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
    knownGroupPaths.current = currentPaths;
  }, [groups, season]);

  useEffect(() => {
    const clearActive = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setActivePath(null);
    };
    document.addEventListener("pointerdown", clearActive);
    return () => document.removeEventListener("pointerdown", clearActive);
  }, []);

  const toggleRoutes = (targetRoutes: RoutesPageRoute[]) => {
    const ids = new Set(targetRoutes.map((route) => route._id));
    const allSelected =
      ids.size > 0 && Array.from(ids).every((id) => selectedRouteIds.has(id));
    const next = new Set(selectedRouteIds);
    for (const id of Array.from(ids)) {
      if (allSelected) next.delete(id);
      else next.add(id);
    }
    onSelectionChange(next);
  };

  const toggleExpanded = (key: string) => {
    const fullKey =
      key === "__season__" ? `${season}:__season__` : `${season}:${key}`;
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(fullKey)) next.delete(fullKey);
      else next.add(fullKey);
      return next;
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (busy || event.canceled) return;
    const source = event.operation?.source;
    const target = event.operation?.target;
    const sourceData = source?.data as
      | { routeId?: string; season?: Season; sourcePath?: string | null }
      | undefined;
    const targetData = target?.data as
      | {
          routeId?: string;
          season?: Season;
          sourcePath?: string | null;
          path?: string | null;
        }
      | undefined;
    if (
      source?.type !== "group-route" ||
      !sourceData?.routeId ||
      sourceData.season !== season ||
      targetData?.season !== season
    )
      return;
    const route = routes.find(
      (candidate) => candidate._id === sourceData.routeId,
    );
    if (!route) return;

    const sortableSource = source as typeof source & {
      index?: number;
      sortable?: { group?: unknown; initialGroup?: unknown };
    };
    const projectedGroup = parseSortableGroupKey(
      sortableSource.sortable?.group,
    );
    const initialGroup = parseSortableGroupKey(
      sortableSource.sortable?.initialGroup,
    );
    const projectedInSeason = projectedGroup?.season === season;
    const initialInSeason = initialGroup?.season === season;
    const result = resolveGroupDragEnd({
      sourcePath: sourceData.sourcePath ?? null,
      sourceRouteId: route._id,
      targetType: typeof target?.type === "string" ? target.type : undefined,
      targetPath: targetData?.path,
      targetRouteId: targetData?.routeId,
      targetRoutePath: targetData?.sourcePath,
      projectedPath: projectedInSeason ? projectedGroup.path : undefined,
      initialPath: initialInSeason ? initialGroup.path : undefined,
      projectedIndex: sortableSource.index,
      routesInInitialGroup: initialInSeason
        ? routesAtPath(groups, ungrouped, initialGroup.path)
        : [],
    });
    if (!result) return;
    if (result.action === "move") {
      onMoveRoute(route, result.sourcePath, result.targetPath);
      return;
    }
    onReorderRoute(route, result.targetRouteId);
  };

  const rootSiblingNames = groups.map((group) => group.name);
  return (
    <DragDropProvider
      key={dragLayoutKey}
      onBeforeDragStart={(event) => {
        if (busy) event.preventDefault();
      }}
      onDragEnd={onDragEnd}
    >
      <div
        ref={rootRef}
        className="w-full"
        data-testid={`routes-group-tree-${season}`}
        onPointerDownCapture={(event) => {
          const target = event.target as Element;
          if (!target.closest("[data-group-row-path]")) setActivePath(null);
        }}
      >
        <SeasonDropRow
          season={season}
          routes={routes}
          selectedRouteIds={selectedRouteIds}
          busy={busy}
          open={seasonOpen}
          siblingNames={rootSiblingNames}
          onToggleExpanded={() => toggleExpanded("__season__")}
          onToggleRoutes={() => toggleRoutes(routes)}
          onAddGroup={onAddGroup}
        />
        {seasonOpen ? (
          <ul className="mt-1 list-none">
            {groups.map((node) => (
              <GroupDropRow
                key={node.path}
                node={node}
                season={season}
                depth={1}
                expanded={expanded}
                activePath={activePath}
                siblingNames={rootSiblingNames}
                busy={busy}
                pendingRouteId={pendingRouteId}
                selectedRouteIds={selectedRouteIds}
                onToggleExpanded={toggleExpanded}
                onToggleRoutes={toggleRoutes}
                onActivate={setActivePath}
                onAdd={onAddGroup}
                onRename={onRenameGroup}
                onDelete={onDeleteGroup}
                onToggleRoute={(route) => toggleRoutes([route])}
                onReorderRoute={onReorderRoute}
                onOpenRoute={onOpenRoute}
              />
            ))}
            {ungrouped.map((route, index) => (
              <RouteRow
                key={`${route._id}-ungrouped`}
                route={route}
                index={index}
                depth={0}
                sourcePath={null}
                selected={selectedRouteIds.has(route._id)}
                busy={busy}
                saving={pendingRouteId === route._id}
                onToggle={() => toggleRoutes([route])}
                onOpen={() => onOpenRoute(route)}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </DragDropProvider>
  );
}
